import { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import ScreenWithSidebar from "../components/ScreenWithSidebar.jsx";

const DocumentationUrl = new URL("./Documentation-v3.md", import.meta.url);

const slugify = (text) => {
    const slug = text
        .toLowerCase()
        .replace(/[`*_~()[\]<>]/g, "")
        .replace(/[^a-z0-9\s-]/g, "")
        .trim()
        .replace(/\s+/g, "-");

    return slug || "section";
};

const extractHeadings = (markdownText) => {
    if (!markdownText) {
        return [];
    }

    const lines = markdownText.split("\n");
    const headingCounts = new Map();

    return lines
        .map((line) => line.match(/^(#{1,2})\s+(.+)$/))
        .filter(Boolean)
        .map((match) => {
            const level = match[1].length;
            const text = match[2].trim().replace(/\s+#*$/, "");
            const baseSlug = slugify(text);
            const occurrence = headingCounts.get(baseSlug) || 0;
            headingCounts.set(baseSlug, occurrence + 1);

            return {
                id: occurrence === 0 ? baseSlug : `${baseSlug}-${occurrence}`,
                text,
                level,
            };
        });
};

const SidebarPanel = ({ headings }) =>
    <ul className="space-y-2 font-medium">
        {
            headings.map((heading) => (
                <li key={heading.id}>
                    <a
                        href={`#${heading.id}`}
                        className={`flex items-center p-2 text-gray-900 rounded-lg hover:bg-gray-100 ${heading.level === 2 ? "pl-6 text-sm" : ""}`}
                        onClick={(event) => {
                            event.preventDefault();
                            const target = document.getElementById(heading.id);

                            if (target) {
                                target.scrollIntoView({ behavior: "smooth", block: "start" });
                                window.history.replaceState(null, "", `#${heading.id}`);
                            }
                        }}
                    >
                        {heading.text}
                    </a>
                </li>
            ))
        }
    </ul>

const DocumentationPage = () => {
    const [documentation, setDocumentation] = useState(null);
    const [error, setError] = useState(null);
    const headingRenderIndex = useRef(0);

    const headings = useMemo(() => extractHeadings(documentation), [documentation]);
    headingRenderIndex.current = 0;

    const markdownComponents = useMemo(() => {
        const getNextHeadingId = () => {
            const nextHeading = headings[headingRenderIndex.current];
            headingRenderIndex.current += 1;
            return nextHeading ? nextHeading.id : undefined;
        };

        return {
            h1: ({ node, ...props }) => <h1 id={getNextHeadingId()} className="text-xl mt-10 mb-1 font-semibold scroll-mt-20" {...props} />,
            h2: ({ node, ...props }) => <h2 id={getNextHeadingId()} className="text-lg mt-2 font-medium scroll-mt-20" {...props} />,
            p: ({ node, ...props }) => <p className="mb-4" {...props} />,
            a: ({ node, ...props }) => <a className="cursor-pointer hover:underline text-blue-600" {...props} />,
        };
    }, [headings]);

    useEffect(() => {
        const fetchDocumentation = async () => {
            try {
                const response = await fetch(DocumentationUrl);
                if (!response.ok) {
                    throw new Error(`Failed to fetch documentation: ${response.status}`);
                }
                const text = await response.text();
                setDocumentation(text);
            } catch (err) {
                setError(err instanceof Error ? err.message : "Unknown error");
            }
        };
        fetchDocumentation();
    }, []);

    if (error) {
        return (
            <ScreenWithSidebar sidebarPanel={<SidebarPanel headings={[]} />}>
                <div className="w-2/3 mx-auto text-red-600">
                    <h1 className="text-2xl font-bold mb-4">Error Loading Documentation</h1>
                    <p>{error}</p>
                </div>
            </ScreenWithSidebar>
        );
    }

    if (!documentation) {
        return (
            <ScreenWithSidebar sidebarPanel={<SidebarPanel headings={[]} />}>
                <div className="w-2/3 mx-auto">
                    <h1 className="text-xl mt-3 mb-1 font-semibold">Loading Documentation...</h1>
                </div>
            </ScreenWithSidebar>
        );
    }

    return (
        <ScreenWithSidebar sidebarPanel={<SidebarPanel headings={headings} />}>
            <div className="w-2/3 mx-auto prose">
                <ReactMarkdown components={markdownComponents}>
                    {documentation}
                </ReactMarkdown>
            </div>
        </ScreenWithSidebar>
    );
};

export default DocumentationPage;
